"use client";

import React from "react";
import { Month } from "@/types/dto/search-filters/month-picker";
import { groupMonthsByYear } from "@/factories/search-filters/search-filters-factory";
import {Stack, Typography, Grid} from "@mui/material";
import { ColletteButton, ColletteDivider, ColletteTextButton } from "@collette-travel/collette-common-controls";
import { useTranslations } from "next-intl";
import { formatMonthFromId } from "@/components/search-filters/search-filter-utils";


interface MonthPickerProps {
  months: Month[];
  selectedMonths: string[];
  onChange?: (data: Record<string, string>, title?: string) => void;
  clearMonths?: (fieldName: string) => void;
}

const MonthPicker = ({
  months,
  selectedMonths,
  onChange,
  clearMonths
}: MonthPickerProps) => {
  const t = useTranslations("SEARCH");

  // Group months by year from state
  const groupedMonths = groupMonthsByYear(months);

  // Handle click to toggle selected state
  const handleMonthClick = (selectedMonth: Month) => {
    if (onChange) {
      onChange({ "months": formatMonthFromId(selectedMonth.id)}, t("When"));
    }
  };

  const handleClearDates = () => {
    if (clearMonths) {
      clearMonths("months");
    }
  };

  return (
    <Stack
      sx={{
        gap: "1.8rem",
        padding: "1rem 0.8rem",
        border: "1px solid",
        borderColor: "divider",
        alignItems: "flex-end",
        borderRadius: "1rem",
        width: {
          xs: 286,
          legacyTablet: 286,
          legacyDesktop: "100%"
        }
      }}
      data-testid="month-picker"
    >
      {groupedMonths.map((year, index) => (
        <Stack key={year[0].year} gap="1.8rem" width="100%" alignItems="flex-start">
          <Typography variant="h6">{year[0].year}</Typography>
          <Grid container spacing={1} width="100%">
            {year.map((month) => {
              const isSelected = selectedMonths.includes(formatMonthFromId(month.id));
              return (
                <Grid key={month.id} size={3}>
                  <ColletteTextButton
                    size="small"
                    label={month.label.slice(0, 3)}
                    testId={`month-picker_${month.id}_button`}
                    disabled={!isSelected && month.disabled}
                    sx={{
                      fontSize: "1.4rem!important",
                      width: "100%",
                      "&:focus-visible": {
                        backgroundColor: "divider"
                      },
                      ...(isSelected && {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        fontSize: "1.4rem!important",
                        "&:focus-visible": {
                          backgroundColor: "primary.light"
                        }
                      })
                    }}
                    onClick={() => handleMonthClick(month)}
                  />
                </Grid>
              );
            })}
          </Grid>
          {index !== groupedMonths.length - 1 && <ColletteDivider sx={{ width: "100%" }} testId={`month-picker_${year[0].year}_divider`} />}
        </Stack>
      ))}
      <ColletteButton
        variant="outlined"
        size="large"
        label={t("Clear Dates")}
        testId="month-picker_clear-dates-button"
        onClick={handleClearDates}
      />
    </Stack>
  );
};

export default MonthPicker;